import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, CheckCircle2, MoreVertical, X } from "lucide-react";
import { RoutePath } from "@/routes";
import { AISparkles } from "@/components/ui/ai-sparkles";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CountBadge } from "@/components/shared/CountBadge";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/services/api.service";
import {
  commitPrioritizedInCache,
  invalidateSeoStrategyRecommendations,
  useAddGroupingToMasterList,
  useCompleteSeoStrategyRecommendation,
  useDismissSeoStrategyRecommendation,
  useGetSeoStrategyRecommendations,
  usePrioritizeSeoStrategyRecommendation,
  useRemoveSeoStrategyRecommendation,
  useRestoreSeoStrategyRecommendation,
  useUpdateSeoStrategyRecommendation,
} from "@/resources/seo-strategy";
import { DismissRecommendationDialog } from "./DismissRecommendationDialog";
import { StrategyCard, type StrategyCardEditSession } from "./StrategyCard";
import { StrategyCardFlight } from "./StrategyCardFlight";
import { StrategyFilterChips, type StrategyFilterValue } from "./StrategyFilterChips";
import type { SeoStrategyEditBody, SeoStrategyRecommendation } from "@/types/seo-strategy";

/** 350ms flight + 800ms hold, `components/seo-strategy.md § Prioritize animation`. */
const PRIORITIZE_TOAST_DELAY_MS = 800;

export type Segment = "prioritized" | "other";

export interface StrategyBrowserProps {
  asin: string;
  productId: number;
  /**
   * The Brand Context toast reads "for {brandName}" -- "this brand" when this is empty
   * (`productDetail` already carries it, `types/product.ts`).
   */
  brandName: string;
  /**
   * Absent for a viewer and wherever the host has nothing to start a test from. WEB-009
   * wires this to A/B-test creation; this task only decides which cards it reaches.
   */
  onStartTest?: (recommendation: SeoStrategyRecommendation) => void;
  /**
   * WEB-001/FR-13: A/B creation only. A `prioritized` card's actions slot renders `Use
   * this strategy` before the kebab when this is passed and the user is not a `viewer`.
   * The product Strategy tab passes neither this nor `isApplyingStrategy`, so the button
   * cannot appear there (`decisions/the-strategy-browser-takes-its-ab-creation-action-and-segment-as-props.md`
   * decision 1).
   */
  onUseStrategy?: (recommendation: SeoStrategyRecommendation) => void;
  /** Disables every `Use this strategy` button while true. */
  isApplyingStrategy?: boolean;
  /**
   * WEB-001/FR-4: A/B creation only. When passed, the browser renders this segment and
   * routes every internal move through `onSegmentChange` instead of its own setter.
   * Absent -> internal state, exactly as today. The chip stays internal either way.
   */
  segment?: Segment;
  onSegmentChange?: (segment: Segment) => void;
}

/** The dialog serves both `Dismiss` (pending) and `Remove strategy` (prioritized). */
type ReasonDialogState = { kind: "dismiss" | "remove"; recommendation: SeoStrategyRecommendation };

/**
 * `EmptyActionState size="compact"` (`prototype-extracted.md § 2.5`) -- no shared component
 * for it exists yet, so this rebuilds its classes directly rather than forking a primitive.
 */
function ListEmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 rounded-sm border border-border bg-card px-6 py-8 text-center">
      <div className="space-y-1.5">
        <p className="text-base font-semibold text-foreground">{title}</p>
        <p className="mx-auto max-w-sm text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

/** `outcomeTag` decides the mark; a `null` tag (only possible on a legacy row) falls back to `status`. */
function markLabel(recommendation: SeoStrategyRecommendation): "Won" | "Resolved" | "Completed" {
  if (recommendation.outcomeTag === "won") return "Won";
  if (recommendation.outcomeTag === "resolved") return "Resolved";
  if (recommendation.outcomeTag === "manual") return "Completed";
  return recommendation.status === "resolved" ? "Resolved" : "Completed";
}

/** A completed or resolved card's only control -- a status marker, not an action. */
function CompletedMark({
  recommendation,
  productId,
}: {
  recommendation: SeoStrategyRecommendation;
  productId: number;
}) {
  const label = markLabel(recommendation);
  const badge = (
    <Badge className="gap-1 border-0 bg-status-success-soft font-semibold text-foreground">
      <CheckCircle2 className="h-3 w-3" />
      {label}
    </Badge>
  );

  // Only "Won" is backed by a real destination -- a linked A/B test actually won. Reuses
  // the route today's completed-strategies log links to.
  if (label !== "Won") return badge;

  return <Link to={RoutePath.ProductDetailAbTests.replace(":productId", String(productId))}>{badge}</Link>;
}

interface RecommendationHandlers {
  onDismiss: (recommendation: SeoStrategyRecommendation) => void;
  /** Takes the click's `currentTarget` too -- WEB-007 needs the card's rect at the click. */
  onPrioritize: (recommendation: SeoStrategyRecommendation, triggerElement: HTMLElement) => void;
  /** WEB-005: adds the card's id to the editing set. */
  onEdit: (recommendation: SeoStrategyRecommendation) => void;
  onRemove: (recommendation: SeoStrategyRecommendation) => void;
  onComplete: (recommendation: SeoStrategyRecommendation) => void;
  onRestore: (recommendation: SeoStrategyRecommendation) => void;
  /** WEB-006: the dismissed cards whose own `Restore` request is in flight -- a set, so
   *  overlapping restores on different cards each disable only their own button. */
  restoringIds: ReadonlySet<number>;
  /**
   * WEB-001/FR-13: present only when the host wants `Use this strategy` on this card
   * (absent for the product Strategy tab, and for a viewer). A direct pass-through of the
   * host's own handler -- this component holds no state for it.
   */
  onUseStrategy?: (recommendation: SeoStrategyRecommendation) => void;
  isApplyingStrategy: boolean;
}

/**
 * Computes the header action cluster for one card from its state and the viewer rule alone
 * (`decisions/the-strategy-card-is-a-shell-with-an-action-slot.md` decision 4) -- the only
 * place in this file that builds an action node. A `viewer` never reaches the branches that
 * build `Dismiss`, `Prioritize` or the kebab; the mark badge is not an action and still
 * renders for one.
 */
function actionsForRecommendation(
  recommendation: SeoStrategyRecommendation,
  isViewer: boolean,
  productId: number,
  handlers: RecommendationHandlers,
): ReactNode {
  const { status } = recommendation;

  if (status === "completed" || status === "resolved") {
    return <CompletedMark recommendation={recommendation} productId={productId} />;
  }

  if (isViewer) return null;

  if (status === "pending") {
    return (
      <>
        <Button variant="ghost" size="sm" onClick={() => handlers.onDismiss(recommendation)}>
          <X className="h-4 w-4" />
          Dismiss
        </Button>
        <Button size="sm" onClick={(event) => handlers.onPrioritize(recommendation, event.currentTarget)}>
          <Check className="h-4 w-4" />
          Prioritize
        </Button>
      </>
    );
  }

  if (status === "prioritized") {
    const items = [
      { label: "Edit strategy", onClick: () => handlers.onEdit(recommendation) },
      { label: "Remove strategy", onClick: () => handlers.onRemove(recommendation) },
      { label: "Complete strategy", onClick: () => handlers.onComplete(recommendation) },
    ];
    return (
      <>
        {handlers.onUseStrategy ? (
          <Button
            variant="ghost"
            size="sm"
            className="whitespace-nowrap"
            disabled={handlers.isApplyingStrategy}
            onClick={() => handlers.onUseStrategy?.(recommendation)}
          >
            Use this strategy
          </Button>
        ) : null}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Strategy actions">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {items.map((item) => (
              <DropdownMenuItem key={item.label} onClick={item.onClick}>
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </>
    );
  }

  // dismissed -- WEB-006, `components/seo-strategy.md § The screen` § `Restore`: exactly
  // this markup, read from the prototype by the design critic -- no className, no icon.
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={handlers.restoringIds.has(recommendation.id)}
      onClick={() => handlers.onRestore(recommendation)}
    >
      Restore
    </Button>
  );
}

/** Bookkeeping for one in-flight `Prioritize`, kept out of state -- only `flyingIds` and
 * `celebrating` below need to trigger a render. */
interface PrioritizeTracking {
  recommendation: SeoStrategyRecommendation;
  sourceRect: DOMRect;
  /** Set once `onLand` has fired for this id. */
  landed: boolean;
  outcome: "pending" | "success" | "error";
  /** Set once the 800ms-after-landing timer has fired while the request was still pending. */
  timerElapsed: boolean;
}

/**
 * The strategy browser (`components/seo-strategy.md § The screen`, *The strategy browser*).
 * Owns the lists, the counts, the mutations, the reason dialog and the page switch-over
 * (`decisions/the-strategy-card-is-a-shell-with-an-action-slot.md` decision 4).
 */
export function StrategyBrowser({
  asin,
  productId,
  brandName,
  onStartTest,
  onUseStrategy,
  isApplyingStrategy,
  segment: controlledSegment,
  onSegmentChange,
}: StrategyBrowserProps) {
  // WEB-001/FR-4: when the host controls the segment (A/B creation), every internal move
  // below routes through `onSegmentChange` instead of this state -- no new hook added,
  // this is the same single `useState` the browser has always held for the segment.
  const [internalSegment, setInternalSegment] = useState<Segment>("prioritized");
  const segment = controlledSegment ?? internalSegment;
  const setSegment = (value: Segment) => {
    if (onSegmentChange) {
      onSegmentChange(value);
    } else {
      setInternalSegment(value);
    }
  };
  const [filter, setFilter] = useState<StrategyFilterValue>("recommended");
  const [reasonDialog, setReasonDialog] = useState<ReasonDialogState | null>(null);
  // AUT-460: `reasonDialog` goes `null` the instant the dialog is told to close, but Radix
  // keeps `DialogContent` mounted and animating out for another beat (`data-state="closed"`
  // with a CSS exit transition). If the title/button read `reasonDialog?.kind` directly they
  // would flip from "dismissing" to "removing" (the `?? "remove"` fallback) mid-close. This
  // ref holds whatever kind was last shown so the closing dialog keeps its own copy.
  const lastReasonDialogKindRef = useRef<"dismiss" | "remove">("remove");
  if (reasonDialog) {
    lastReasonDialogKindRef.current = reasonDialog.kind;
  }
  const isViewer = useViewerReadOnly();
  const { data } = useGetSeoStrategyRecommendations(asin);

  const prioritizeMutation = usePrioritizeSeoStrategyRecommendation();
  const dismissMutation = useDismissSeoStrategyRecommendation();
  const removeMutation = useRemoveSeoStrategyRecommendation();
  const completeMutation = useCompleteSeoStrategyRecommendation();
  const addToMasterListMutation = useAddGroupingToMasterList();
  const restoreMutation = useRestoreSeoStrategyRecommendation();
  const updateMutation = useUpdateSeoStrategyRecommendation();

  const pending = data?.pending ?? [];
  // `useMemo`, not the plain `data?.prioritized ?? []` every other list here uses: the
  // pruning effect below depends on this array's identity, and `?? []` builds a fresh
  // array every render (even when `data.prioritized` itself hasn't changed), which reran
  // the effect on every render, not just when the prioritized list actually changed.
  const prioritized = useMemo(() => data?.prioritized ?? [], [data?.prioritized]);
  const completed = data?.completed ?? [];
  const dismissed = data?.dismissed ?? [];

  const prioritizedCount = prioritized.length;
  const otherCount = pending.length + completed.length + dismissed.length;

  // WEB-007: which cards currently have a flight in progress (drives the invisible
  // wrapper and mounts the ghost) and whether the Prioritized badge is popping. Timing
  // bookkeeping that does not itself need a render lives in the ref below, keyed by
  // recommendation id.
  const [flyingIds, setFlyingIds] = useState<ReadonlySet<number>>(new Set());
  const [celebrating, setCelebrating] = useState(false);
  // WEB-005: which prioritized cards are in edit mode -- a set of ids, so several cards
  // edit at once and independently (intent *Open questions* § 11). The draft itself lives
  // in each `StrategyCard`, not here.
  const [editingIds, setEditingIds] = useState<ReadonlySet<number>>(new Set());
  // W-3: which editing cards have a Save in flight -- this component's own bookkeeping,
  // independent of `updateMutation.isPending`/`.variables` (see `handleSaveEdit`).
  const [savingIds, setSavingIds] = useState<ReadonlySet<number>>(new Set());
  // Same reason as `savingIds`: `restoreMutation.isPending`/`.variables` reflect only the
  // shared observer's last call, so a second card's Restore would read the first card's
  // in-flight state (or vice versa) rather than its own.
  const [restoringIds, setRestoringIds] = useState<ReadonlySet<number>>(new Set());
  const prioritizeTrackingRef = useRef<Map<number, PrioritizeTracking>>(new Map());
  const toastTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const prioritizedBadgeRef = useRef<HTMLSpanElement>(null);
  const prioritizedTabRef = useRef<HTMLButtonElement>(null);

  useEffect(
    () => () => {
      toastTimersRef.current.forEach((timer) => clearTimeout(timer));
    },
    [],
  );

  // WEB-005: an id leaves the editing set whenever that strategy is no longer in the
  // cached `prioritized` list -- a Remove or Complete from the same kebab mid-edit, or a
  // refetch that moved it. `Save`'s own success/409 paths remove their id explicitly
  // (below); this is the catch-all for every other way a card stops being prioritized.
  useEffect(() => {
    setEditingIds((previous) => {
      if (previous.size === 0) return previous;
      let changed = false;
      const next = new Set<number>();
      previous.forEach((id) => {
        if (prioritized.some((item) => item.id === id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, [prioritized]);

  const removeFlying = (id: number) => {
    setFlyingIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const showPrioritizedToast = (recommendation: SeoStrategyRecommendation) => {
    // `toast()` (`@/hooks/use-toast`) takes no `id` -- unlike the design capture's
    // `toast.success(msg, { id })` -- but its own TOAST_LIMIT of 1 already means only one
    // toast is ever shown, so a stable id would add nothing here; our own bookkeeping
    // below (deleting the tracking entry once fired) is what guarantees this fires once.
    toast({ title: `Success! The ${recommendation.gapTitle} has been prioritized.` });
  };

  const handleSegmentChange = (value: string) => {
    setSegment(value as Segment);
    // FR-5: selecting Other strategies always selects Recommended, even when it was
    // already the active segment's last-seen chip.
    if (value === "other") setFilter("recommended");
  };

  const reviewRecommendations = () => {
    setSegment("other");
    setFilter("recommended");
  };

  const settlePrioritize = (id: number, outcome: "success" | "error") => {
    const tracking = prioritizeTrackingRef.current.get(id);
    if (!tracking) return;

    if (outcome === "error") {
      const timer = toastTimersRef.current.get(id);
      if (timer) {
        clearTimeout(timer);
        toastTimersRef.current.delete(id);
      }
      prioritizeTrackingRef.current.delete(id);
      // The hook's own onError already rolled the cache back to the pre-click snapshot.
      // If landing hasn't happened yet, unhide the card so it reappears under
      // Recommended; if it has, the rollback already put it there.
      if (!tracking.landed) removeFlying(id);
      return;
    }

    tracking.outcome = "success";
    if (tracking.landed) {
      invalidateSeoStrategyRecommendations(asin);
      if (tracking.timerElapsed) {
        showPrioritizedToast(tracking.recommendation);
        prioritizeTrackingRef.current.delete(id);
      }
      // else: the running 800ms timer will see outcome === "success" when it fires.
    }
    // else: not landed yet -- landing will invalidate immediately, since success is
    // already known by then.
  };

  const handleLand = (id: number) => {
    const tracking = prioritizeTrackingRef.current.get(id);
    removeFlying(id);

    if (!tracking) return;
    if (tracking.outcome === "error") {
      prioritizeTrackingRef.current.delete(id);
      return;
    }

    commitPrioritizedInCache(asin, tracking.recommendation);
    tracking.landed = true;
    setCelebrating(true);

    if (tracking.outcome === "success") {
      invalidateSeoStrategyRecommendations(asin);
    }

    const timer = setTimeout(() => {
      toastTimersRef.current.delete(id);
      const current = prioritizeTrackingRef.current.get(id);
      if (!current) return;
      if (current.outcome === "success") {
        showPrioritizedToast(current.recommendation);
        prioritizeTrackingRef.current.delete(id);
      } else {
        current.timerElapsed = true;
      }
    }, PRIORITIZE_TOAST_DELAY_MS);
    toastTimersRef.current.set(id, timer);
  };

  // `prioritizeMutation`/`completeMutation`/`restoreMutation` are each one shared observer
  // across every card (same root cause as `updateMutation`, see the W-3 comment on
  // `handleSaveEdit`) -- a per-call `{ onSuccess, onError }` passed to `.mutate()` is
  // replaced by the next card's call, so a second Prioritize/Complete/Restore started while
  // the first is still in flight silently drops the first card's own callback. `mutateAsync`
  // ties the result to that call's own promise instead, so each card's own `try`/`catch`
  // fires regardless of what any other card does in the meantime.
  const handlePrioritize = async (recommendation: SeoStrategyRecommendation, triggerElement: HTMLElement) => {
    const wrapper =
      triggerElement.closest<HTMLElement>(`[data-strategy-card-wrapper="${recommendation.id}"]`) ?? triggerElement;
    const sourceRect = wrapper.getBoundingClientRect();

    prioritizeTrackingRef.current.set(recommendation.id, {
      recommendation,
      sourceRect,
      landed: false,
      outcome: "pending",
      timerElapsed: false,
    });
    setFlyingIds((prev) => new Set(prev).add(recommendation.id));

    // The snapshot (for rollback) is taken at click by the hook's own onMutate; the
    // optimistic move here happens later, at landing (`handleLand`), not at settle.
    try {
      await prioritizeMutation.mutateAsync({ id: recommendation.id, asin });
      settlePrioritize(recommendation.id, "success");
    } catch {
      settlePrioritize(recommendation.id, "error");
    }
  };

  const handleComplete = async (recommendation: SeoStrategyRecommendation) => {
    try {
      await completeMutation.mutateAsync({ id: recommendation.id, asin });
      setSegment("other");
      setFilter("completed");
      toast({ title: "Strategy marked complete." });
    } catch {
      // The hook's own onError (handleApiError, onSettled invalidate) already ran.
    }
  };

  const handleRestore = async (recommendation: SeoStrategyRecommendation) => {
    const { id } = recommendation;
    setRestoringIds((previous) => new Set(previous).add(id));
    try {
      await restoreMutation.mutateAsync({ id, asin });
      setSegment("other");
      setFilter("recommended");
      toast({ title: "Strategy restored to Recommended." });
    } catch {
      // The hook's own onError already ran.
    } finally {
      setRestoringIds((previous) => {
        if (!previous.has(id)) return previous;
        const next = new Set(previous);
        next.delete(id);
        return next;
      });
    }
  };

  const handleEditStrategy = (recommendation: SeoStrategyRecommendation) => {
    setEditingIds((prev) => {
      if (prev.has(recommendation.id)) return prev;
      return new Set(prev).add(recommendation.id);
    });
  };

  const handleCancelEdit = (id: number) => {
    setEditingIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  /**
   * WEB-005 (`components/seo-strategy.md § The screen`, the `Save` outcomes table):
   * `200` leaves the set and toasts `Strategy updated.` -- the hook's own `onSuccess`
   * already merged the saved values into the cache. `409` also leaves the set (the draft
   * is dropped) but shows no toast of its own -- the hook's `onError` already toasted the
   * server's message and invalidated. `422`, a network error and a `5xx` leave the id in
   * the set: the card stays open with its draft, which `StrategyCard` still owns.
   *
   * W-3: one `useUpdateSeoStrategyRecommendation()` instance (`updateMutation` above)
   * serves every editing card. In the installed `@tanstack/query-core`,
   * `mutationObserver.mutate()` replaces the observer's own `#mutateOptions` and detaches
   * from whichever mutation was previously in flight on every call -- so a per-call
   * `{ onSuccess, onError }` passed to `.mutate()`, and `.isPending`/`.variables` read off
   * the shared observer, only ever reflect the LAST card that called it. If card A's save
   * is still in flight when card B saves, A's per-call callbacks never fire and A's
   * `isSaving` drops early.
   *
   * `mutateAsync` sidesteps this: its returned promise is tied to that call's own
   * `Mutation` instance, not to the observer's shared per-call options, so each card's own
   * `.then()` fires from its own save regardless of what any other card's save does in the
   * meantime. The hook-level `onSuccess`/`onError` (the cache merge, the 409 invalidation,
   * `handleApiError`) still run for every call, unaffected -- they live on the mutation's
   * own options, baked in at `useUpdateSeoStrategyRecommendation`'s definition, not on the
   * per-call ones.
   *
   * `savingIds` is this component's own per-card "in flight" set (not
   * `updateMutation.isPending`/`.variables`, the same shared observer state the bug above
   * lives in), so `isSaving` -- and the disabled `Save` it drives -- stays correct with
   * several cards saving at once.
   */
  const handleSaveEdit = (recommendation: SeoStrategyRecommendation, body: SeoStrategyEditBody) => {
    const { id } = recommendation;
    setSavingIds((previous) => new Set(previous).add(id));

    updateMutation
      .mutateAsync({ id, asin, ...body })
      .then(
        () => {
          handleCancelEdit(id);
          toast({ title: "Strategy updated." });
        },
        (error) => {
          if (error instanceof ApiError && error.status === 409) {
            handleCancelEdit(id);
          }
          // 422, a network error or a 5xx: leave `id` in `editingIds` -- the card stays
          // open with its draft, which `StrategyCard` still owns.
        },
      )
      .finally(() => {
        setSavingIds((previous) => {
          if (!previous.has(id)) return previous;
          const next = new Set(previous);
          next.delete(id);
          return next;
        });
      });
  };

  const handleReasonConfirm = (reason: string, notes?: string) => {
    if (!reasonDialog) return;

    const { kind, recommendation } = reasonDialog;
    const mutation = kind === "dismiss" ? dismissMutation : removeMutation;

    mutation.mutate(
      { id: recommendation.id, asin, reason, notes },
      {
        onSuccess: () => {
          // Close only if the dialog still open at settle is still this same card's -- a
          // Cancel followed by opening a different card's dialog before this one settles
          // must not close that later dialog out from under the user.
          setReasonDialog((prev) => (prev?.recommendation.id === recommendation.id ? null : prev));
          toast({
            title: "Feedback saved to Brand Context",
            description: `Signal AI will learn from this for ${brandName || "this brand"}.`,
          });
        },
      },
    );
  };

  const renderCard = (recommendation: SeoStrategyRecommendation) => {
    const isPrioritized = recommendation.status === "prioritized";
    const grouping = recommendation.thematicGrouping;
    const canAddToMasterList =
      isPrioritized &&
      !isViewer &&
      grouping?.status === "suggested" &&
      (grouping?.keywords.length ?? 0) > 0 &&
      (grouping?.name ?? "").trim() !== "";

    // WEB-005: present only while this card's id is in the editing set -- absent, the
    // card renders exactly as before (`StrategyCard`'s own contract for `editSession`).
    const editSession: StrategyCardEditSession | undefined = editingIds.has(recommendation.id)
      ? {
          onSave: (body) => handleSaveEdit(recommendation, body),
          onCancel: () => handleCancelEdit(recommendation.id),
          isSaving: savingIds.has(recommendation.id),
        }
      : undefined;

    return (
      // WEB-007: `invisible` while this card's flight is in progress -- the wrapper's box
      // is kept (nothing reflows) while the ghost stands in for it.
      <div
        key={recommendation.id}
        data-strategy-card-wrapper={recommendation.id}
        className={flyingIds.has(recommendation.id) ? "invisible" : undefined}
      >
        <StrategyCard
          recommendation={recommendation}
          actions={actionsForRecommendation(recommendation, isViewer, productId, {
            onDismiss: (r) => setReasonDialog({ kind: "dismiss", recommendation: r }),
            onPrioritize: handlePrioritize,
            onEdit: handleEditStrategy,
            onRemove: (r) => setReasonDialog({ kind: "remove", recommendation: r }),
            onComplete: handleComplete,
            onRestore: handleRestore,
            restoringIds,
            onUseStrategy,
            isApplyingStrategy: isApplyingStrategy ?? false,
          })}
          editSession={editSession}
          onStartTest={isPrioritized && !isViewer && onStartTest ? () => onStartTest(recommendation) : undefined}
          onAddGroupingToMasterList={
            canAddToMasterList
              ? async () => {
                  try {
                    await addToMasterListMutation.mutateAsync({ id: recommendation.id, asin });
                    toast({
                      title: "Added to Master List",
                      description: `"${grouping?.name}" is now a thematic grouping on this product.`,
                    });
                  } catch {
                    // The hook's own onError (handleApiError) already ran.
                  }
                }
              : undefined
          }
        />
      </div>
    );
  };

  let slotThree: ReactNode;

  if (segment === "prioritized") {
    if (prioritizedCount > 0) {
      slotThree = <>{prioritized.map(renderCard)}</>;
    } else if (pending.length > 0) {
      slotThree = (
        <Alert variant="ai" className="rounded-md">
          <AISparkles className="h-4 w-4" />
          <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
            <AlertTitle className="mb-0 min-w-[12rem] flex-1 leading-5">
              You have {pending.length} recommended {pending.length === 1 ? "strategy" : "strategies"}.
            </AlertTitle>
            <Button variant="ghost" size="sm" onClick={reviewRecommendations}>
              Review recommendations <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </Alert>
      );
    } else {
      slotThree = (
        <ListEmptyState
          title="No prioritized strategies yet"
          description="No open recommendations — check back after the next Amazon AI Score refresh."
        />
      );
    }
  } else {
    const activeList = filter === "recommended" ? pending : filter === "completed" ? completed : dismissed;

    if (activeList.length > 0) {
      slotThree = <>{activeList.map(renderCard)}</>;
    } else if (filter === "recommended") {
      slotThree = (
        <ListEmptyState
          title="No new recommended strategies"
          description="Every recommended strategy for this listing has been prioritized or dismissed. New recommendations will show up here when they are available."
        />
      );
    } else if (filter === "completed") {
      slotThree = (
        <ListEmptyState
          title="No completed strategies"
          description="Strategies show here when you complete them or a winning A/B test wraps up."
        />
      );
    } else {
      slotThree = (
        <ListEmptyState
          title="No dismissed strategies"
          description="When you dismiss a strategy, it will be stored here along with the reason for the dismissal."
        />
      );
    }
  }

  return (
    <div className="space-y-6">
      <Tabs value={segment} onValueChange={handleSegmentChange}>
        <TabsList variant="segmented">
          <TabsTrigger ref={prioritizedTabRef} variant="segmented" value="prioritized" className="gap-1.5">
            Prioritized strategies{" "}
            <span ref={prioritizedBadgeRef} className="inline-flex">
              <CountBadge
                size="sm"
                count={prioritizedCount}
                celebrate={celebrating}
                onCelebrateEnd={() => setCelebrating(false)}
              />
            </span>
          </TabsTrigger>
          <TabsTrigger variant="segmented" value="other" className="gap-1.5">
            Other strategies <CountBadge size="sm" count={otherCount} />
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* WEB-007: one ghost per in-flight Prioritize, flying toward the Prioritized count
          badge (falling back to the tab trigger itself). */}
      {Array.from(flyingIds, (id) => {
        const tracking = prioritizeTrackingRef.current.get(id);
        if (!tracking) return null;
        return (
          <StrategyCardFlight
            key={id}
            sourceRect={tracking.sourceRect}
            targetElement={prioritizedBadgeRef.current ?? prioritizedTabRef.current}
            gapTitle={tracking.recommendation.gapTitle ?? ""}
            onLand={() => handleLand(id)}
          />
        );
      })}

      {segment === "other" ? (
        <StrategyFilterChips
          value={filter}
          onChange={setFilter}
          recommendedCount={pending.length}
          completedCount={completed.length}
          dismissedCount={dismissed.length}
        />
      ) : null}

      {slotThree}

      <DismissRecommendationDialog
        open={reasonDialog !== null}
        onOpenChange={(open) => {
          if (!open) setReasonDialog(null);
        }}
        kind={reasonDialog?.kind ?? lastReasonDialogKindRef.current}
        gapTitle={reasonDialog?.recommendation.gapTitle ?? ""}
        reasons={data?.dismissReasons ?? []}
        isPending={reasonDialog?.kind === "dismiss" ? dismissMutation.isPending : removeMutation.isPending}
        onConfirm={handleReasonConfirm}
      />
    </div>
  );
}
